// Kinio KMS 操作台的共用小元件：等級、家戶狀態、現況 / 處理、錄影影響、事故列、事故動作鈕、麵包屑、固定狀態。
// 畫面語彙照 SPEC #kinio-kms-ui「畫面語彙」與設計系統：每個狀態都是圖示 + 文字；處理標記純文字不畫框；一列只有一顆實心主按鈕。
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { timeStamp } from '../../lib/timefmt';
import { kmgrApi } from './api';
import { useHomeApi } from './hooks';
import { incidentActions, minutesSince, stateRequest, type IncidentAction } from './model';
import type { HomeState, HomeStatus, Incident } from './types';
import './kmgr.css';
import { PowerRowLine } from './power/Incident';

export const NS = 'kmgr';

const SEV_ICON = { P1: '▲', P2: '◆', P3: '●' } as const;
const HOME_ICON: Record<HomeState, string> = { bad: '✕', attention: '!', ok: '✓', unreadable: '⊘', unset: '○', maintenance: '⏸' };

export function SevMark({ sev }: { sev: Incident['severity'] }) {
  const { t } = useTranslation(NS);
  return (
    <span className={'km-sev ' + sev} data-sev={sev}>
      <span aria-hidden>{SEV_ICON[sev]} </span>
      {t(`sev.${sev}`)}
    </span>
  );
}

export function HomeStatePill({ state, until }: { state: HomeState; until?: string }) {
  const { t } = useTranslation(NS);
  return (
    <span className={'km-st ' + state} data-home-state={state}>
      <span aria-hidden>{HOME_ICON[state]}</span>
      {state === 'maintenance' && until ? t('home.maintenance_until', { time: timeStamp(until, undefined, false) }) : t(`home.${state}`)}
    </span>
  );
}

export function homeStateIcon(s: HomeState): string {
  return HOME_ICON[s];
}

export function personName(t: (k: string, o?: Record<string, unknown>) => string, p: { user_id: string; display_name?: string } | 'system' | undefined, me?: string): string {
  if (!p) return '';
  if (p === 'system') return t('person.system');
  if (me && p.user_id === me) return t('person.you');
  return p.display_name || t('person.someone');
}

/** 現況：● 進行中 · N 分鐘 / ↺ 已恢復 hh:mm。 */
export function NowStateText({ inc }: { inc: Incident }) {
  const { t } = useTranslation(NS);
  if (inc.now_state === 'recovered')
    return (
      <span className="km-now recovered" data-now="recovered">
        <span aria-hidden>↺ </span>
        {t('now.recovered_at', { time: inc.recovered_at ? timeStamp(inc.recovered_at, undefined, false) : '' })}
      </span>
    );
  return (
    <span className="km-now ongoing" data-now="ongoing">
      <span aria-hidden>● </span>
      {t('now.ongoing_for', { n: minutesSince(inc.opened_at) })}
    </span>
  );
}

/** 處理標記：◌ 未處理（粗體）/ ◑ 已看到 / ◉ 〈人〉處理中 / ✓ 已結案（純文字、不畫框）。 */
export function HandlingText({ inc, me, since }: { inc: Incident; me?: string; since?: boolean }) {
  const { t } = useTranslation(NS);
  const who = personName(t, inc.claimed_by, me);
  switch (inc.handling) {
    case 'claimed':
      return (
        <span className="km-hd claimed" data-handling="claimed">
          <span aria-hidden>◉ </span>
          {me && inc.claimed_by?.user_id === me ? t('handling.mine') : t('handling.claimed_by', { name: who })}
          {since && inc.claimed_at ? t('handling.since', { time: timeStamp(inc.claimed_at, undefined, false) }) : ''}
        </span>
      );
    case 'acknowledged':
      return (
        <span className="km-hd acknowledged" data-handling="acknowledged">
          <span aria-hidden>◑ </span>
          {t('handling.acknowledged_by', { name: personName(t, inc.acknowledged_by?.[inc.acknowledged_by.length - 1], me) || t('person.someone') })}
        </span>
      );
    case 'resolved':
      return (
        <span className="km-hd resolved" data-handling="resolved">
          <span aria-hidden>✓ </span>
          {t('handling.resolved')}
        </span>
      );
    default:
      return (
        <span className="km-hd open" data-handling="open">
          <span aria-hidden>◌ </span>
          {inc.now_state === 'recovered' ? t('handling.open_recovered') : t('handling.open')}
        </span>
      );
  }
}

/** 錄影影響一行（每列第一行，先寫錄影）。 */
export function ImpactLine({ inc }: { inc: Incident }) {
  const { t } = useTranslation(NS);
  const ri = inc.recording_impact;
  if (!ri) return null;
  let text: string;
  if (ri.state === 'not_recording') text = t('impact.not_recording', { n: ri.cameras ?? 0, min: ri.minutes ?? minutesSince(inc.opened_at) });
  else if (ri.state === 'gap') text = t('impact.gap', { n: ri.cameras ?? 0, min: ri.minutes ?? 0 });
  else if (ri.state === 'fine') text = ri.note ? t('impact.fine_note', { note: ri.note }) : t('impact.fine');
  else text = ri.note ? t('impact.unknown_note', { note: ri.note }) : t('impact.unknown');
  return (
    <div className={'km-impact ' + ri.state} data-impact={ri.state}>
      <span aria-hidden>▭</span>
      <span>{text}</span>
    </div>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

/** 原因（「SRV-ERP01 的 postgresql 停了」「SRV-ERP01 離線」）：kind + 對象名稱 + 服務名稱；對象沒有名稱時寫「主機」。 */
export function incidentCause(t: TFn, inc: Pick<Incident, 'kind' | 'target_name' | 'subject'>): string {
  const target = inc.target_name || t('target.host');
  return t(`cause.${inc.kind}`, { target, subject: inc.subject ?? '', defaultValue: t('cause.other', { target }) });
}

/** 事故完整標題：（跨組織時組織名）家戶 · 原因 · 影響 N 台（SPEC #kinio-kms-incidents「標題」）。 */
export function incidentTitle(t: TFn, inc: Incident, crossOrg: boolean): string {
  const home = [crossOrg ? inc.org_name : '', inc.home_name].filter(Boolean).join(' ');
  const parts = [home, incidentCause(t, inc)];
  if (inc.affected_count) parts.push(t('incident.affected_n', { n: inc.affected_count }));
  return parts.filter(Boolean).join(' · ');
}

/** 家戶狀態的一行原因（kmgr 只給數字，文案在這裡組）。 */
export function homeReason(t: TFn, h: HomeStatus): string {
  switch (h.state) {
    case 'bad':
    case 'attention':
      return h.incidents?.ongoing ? t('home_reason.incidents', { n: h.incidents.ongoing }) : '';
    case 'unreadable': {
      const n = h.network;
      if (n?.reason === 'snmp_auth_failed') return t('home_reason.snmp_auth');
      const c = n?.collectors_offline?.[0];
      if (c) return t('home_reason.collector_offline', { name: c.name || t('net.collector_unnamed'), n: minutesSince(c.last_seen_at) });
      return h.last_seen_at ? t('home_reason.unreadable_since', { time: timeStamp(h.last_seen_at, undefined, false) }) : t('home_reason.unreadable_never');
    }
    case 'maintenance':
      return h.maintenance_by ? t('home_reason.maintenance_by', { name: h.maintenance_by.display_name || t('person.someone') }) : '';
    case 'unset':
      return t('home_reason.unset');
    default:
      return h.agents?.total ? t('home_reason.watching', { n: h.agents.total }) : '';
  }
}

/** OS 一行（「Ubuntu 24.04」）。 */
export function osText(os: { name: string; version: string } | undefined): string {
  return os ? [os.name, os.version].filter(Boolean).join(' ') : '';
}

export function incidentHref(inc: Pick<Incident, 'home_id' | 'incident_id'>): string {
  return `/kmgr/incidents/${encodeURIComponent(inc.home_id)}/${encodeURIComponent(inc.incident_id)}`;
}

/** 事故列（總覽與清單同一個版型）：等級｜內容（標題 → 錄影影響 → 處理與時長 / 升級 → 同家戶警報）｜動作（最右一欄）。 */
export function IncidentRow({ inc, me, crossOrg, onConflict }: { inc: Incident; me: string; crossOrg: boolean; onConflict?: (msg: string) => void }) {
  const { t } = useTranslation(NS);
  return (
    <li className="km-irow" data-incident={inc.incident_id}>
      <SevMark sev={inc.severity} />
      <div>
        <Link className="km-ititle" to={incidentHref(inc)}>
          {incidentTitle(t, inc, crossOrg)}
        </Link>
        {(inc.recurrence_count ?? 1) >= 3 && (
          <>
            {' '}
            <span className="km-recur">
              <span aria-hidden>! </span>
              {t('incident.recurring', { n: inc.recurrence_count })}
            </span>
          </>
        )}
        <div className="km-iline">
          <ImpactLine inc={inc} />
        </div>
        <PowerRowLine inc={inc} />
        <div className="km-iline km-sub">
          {inc.now_state === 'recovered' && (
            <>
              <NowStateText inc={inc} />
              {' · '}
            </>
          )}
          <HandlingText inc={inc} me={me} />
          {inc.handling !== 'claimed' && inc.handling !== 'resolved' && <span className="muted">{' · ' + t('incident.for_min', { n: minutesSince(inc.opened_at) })}</span>}
          {inc.escalation_note && <span className="muted">{' · ' + inc.escalation_note}</span>}
          {inc.silenced_until && <span className="muted">{' · ' + t('incident.silenced_until', { time: timeStamp(inc.silenced_until, undefined, false), name: personName(t, inc.silenced_by, me) })}</span>}
          {inc.alarms_open ? <span>{' · ' + t('incident.alarms_open', { n: inc.alarms_open })}</span> : null}
        </div>
      </div>
      <div className="km-iact">
        <IncidentButtons inc={inc} me={me} compact onConflict={onConflict} />
      </div>
    </li>
  );
}

const ACTION_KEY: Record<IncidentAction, string> = { claim: 'act.claim', take_over: 'act.take_over', acknowledge: 'act.acknowledge', resolve: 'act.resolve' };

/**
 * 事故動作鈕：一顆實心主要鈕 + 次要鈕；compact（總覽、清單）只放主要鈕。
 * 衝突（409 INCIDENT_STATE_CONFLICT）→ 重抓並告訴呼叫端「已由〈人〉處理」。
 */
export function IncidentButtons({ inc, me, compact, onConflict, onDone }: { inc: Incident; me: string; compact?: boolean; onConflict?: (msg: string) => void; onDone?: (i: Incident) => void }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const { api } = useHomeApi(inc.home_id);
  const { primary, secondary } = incidentActions(inc, me);
  const [busy, setBusy] = useState<IncidentAction | null>(null);
  const [resolving, setResolving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (a: IncidentAction, note?: string) => {
    if (a === 'resolve' && note === undefined) {
      setResolving(true);
      return;
    }
    setBusy(a);
    setErr(null);
    try {
      const r = await kmgrApi.setState(api ?? '', inc.home_id, inc.incident_id, stateRequest(a, inc, me, note));
      onDone?.(r);
    } catch (e) {
      if (isKinioError(e) && e.code === 'INCIDENT_STATE_CONFLICT') {
        // detail.handled_by = user_id（kmgr）；名稱用這件事故帶的 people 查，查不到就不寫人名
        const by = typeof e.detail?.handled_by === 'string' ? e.detail.handled_by : '';
        const name = by ? (by === me ? t('person.you') : inc.people?.[by]) : '';
        onConflict?.(name ? t('conflict.handled_by', { name }) : t('conflict.changed'));
      } else setErr(errText(t, e));
    } finally {
      setBusy(null);
      setResolving(false);
      void qc.invalidateQueries({ queryKey: ['kmgr'] });
    }
  };
  if (!primary) return null;
  return (
    <>
      <button className="btn btn-primary btn-sm" disabled={!!busy || !me} onClick={() => void run(primary)} data-action={primary}>
        {t(ACTION_KEY[primary])}
      </button>
      {!compact &&
        secondary.map((a) => (
          <button key={a} className="btn btn-sm" disabled={!!busy || !me} onClick={() => void run(a)} data-action={a}>
            {t(a === 'resolve' ? 'act.close' : ACTION_KEY[a])}
          </button>
        ))}
      {err && (
        <span className="error small" role="alert">
          {err}
        </span>
      )}
      {resolving && <ResolveDialog onCancel={() => setResolving(false)} onOk={(note) => void run('resolve', note)} busy={busy === 'resolve'} />}
    </>
  );
}

function ResolveDialog({ onCancel, onOk, busy }: { onCancel(): void; onOk(note: string): void; busy: boolean }) {
  const { t } = useTranslation(NS);
  const [note, setNote] = useState('');
  return (
    <Dialog open title={t('resolve.title')} onClose={onCancel} testId="km-resolve">
      <p className="muted">{t('resolve.body')}</p>
      <label className="field">
        <span>{t('resolve.note')}</span>
        <textarea className="input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </label>
      <div className="dialog-actions">
        <button className="btn" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" disabled={busy} onClick={() => onOk(note.trim())}>
          {t('resolve.ok')}
        </button>
      </div>
    </Dialog>
  );
}

/** 錯誤 → 白話（UX-04：發生什麼 / 錯誤碼小字）。 */
export function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  const code = isKinioError(e) ? e.code : 'NETWORK';
  return t(`err.${code}`, { defaultValue: t('err.default', { code }) });
}

export function Crumbs({ items }: { items: { label: string; to?: string }[] }) {
  const { t } = useTranslation(NS);
  return (
    <nav aria-label={t('crumbs')}>
      <ol className="km-crumbs">
        {items.map((it, i) => (
          <li key={i} aria-current={i === items.length - 1 ? 'page' : undefined}>
            {it.to && i < items.length - 1 ? <Link to={it.to}>{it.label}</Link> : it.label}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** 固定狀態：空 / 讀不到 / 找不到；一句為什麼 + 一個動作。 */
export function StateBox({ icon, title, body, children, testId }: { icon: ReactNode; title: string; body?: string; children?: ReactNode; testId?: string }) {
  return (
    <div className="card km-state" data-testid={testId}>
      <div className="ic" aria-hidden>
        {icon}
      </div>
      <h2>{title}</h2>
      {body && <p>{body}</p>}
      {children}
    </div>
  );
}

/** 「即時 · N 秒前更新」/「已中斷 · 最後更新 hh:mm」。 */
export function LiveStamp({ updatedAt, stale }: { updatedAt: number; stale: boolean }) {
  const { t } = useTranslation(NS);
  if (!updatedAt) return null;
  const s = Math.max(0, Math.round((Date.now() - updatedAt) / 1000));
  return (
    <span className={'km-live' + (stale ? ' off' : '')} data-testid="km-live">
      {stale ? t('live.off', { time: timeStamp(updatedAt, undefined, false) }) : s < 60 ? t('live.on_s', { n: s }) : t('live.on_m', { n: Math.floor(s / 60) })}
    </span>
  );
}

/** 品牌名（「〈品牌〉管理程式」用）：console 名稱去掉結尾的「VMS / 管理台」（連字號也算分隔：ls-vms → ls）。 */
export function brandName(displayName: string): string {
  return displayName.replace(/\s*\(mock\)$/i, '').replace(/[\s-]*(VMS|管理台|管理台|Console)\s*$/i, '').trim() || displayName;
}
